/**
 * 认证服务
 * 处理登录、登出、用户信息等 API 调用
 */

import axios from 'axios';

// 使用相对路径，通过前端代理访问后端，确保 cookie 正确传递
const API_BASE_URL = '/api';

// 创建 axios 实例
const authClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,  // 支持 session cookie
});

export interface User {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_superuser: boolean;
    is_staff: boolean;
    role: 'admin' | 'analyst' | 'viewer';
    role_display: string;
    groups: string[];
}

export interface LoginResponse {
    success: boolean;
    user: User;
}

export interface AuthCheckResponse {
    authenticated: boolean;
    user: User | null;
}

export interface RegisterResponse {
    success: boolean;
    user: User;
    organization: {
        id: number;
        name: string;
        role: string;
    };
}

/**
 * 用户登录
 */
export async function login(username: string, password: string): Promise<LoginResponse> {
    const response = await authClient.post<LoginResponse>('/auth/login/', {
        username,
        password,
    });
    return response.data;
}

/**
 * 用户注册
 */
export async function register(username: string, password: string, invitationCode?: string): Promise<RegisterResponse> {
    const response = await authClient.post<RegisterResponse>('/auth/register/', {
        username,
        password,
        invitation_code: invitationCode,
    });
    return response.data;
}

/**
 * 用户登出
 */
export async function logout(): Promise<void> {
    await authClient.post('/auth/logout/');
}

/**
 * 获取当前用户信息
 */
export async function getCurrentUser(): Promise<AuthCheckResponse> {
    const response = await authClient.get<AuthCheckResponse>('/auth/me/');
    return response.data;
}

/**
 * 修改密码
 */
export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await authClient.post('/auth/change-password/', {
        old_password: oldPassword,
        new_password: newPassword,
    });
}

const authService = {
    login,
    logout,
    getCurrentUser,
    changePassword,
};

export default authService;
